import { useEffect, useRef } from 'react';
import { Building2, Wallet, CheckCircle2, AlertCircle, Banknote, Landmark } from 'lucide-react';
import { Button } from '../../ui/button';
import { formatCurrency } from '../HiveFundInvestFlow.utils';

type PaymentMethodValue = 'bank' | 'crypto' | 'wire' | 'investment_account';

interface PaymentMethodStepProps {
  paymentMethod: PaymentMethodValue | null;
  investmentAmount: number;
  onSelect: (method: PaymentMethodValue) => void;
  onContinue: () => void;
  canContinue: boolean;
}

export function PaymentMethodStep({
  paymentMethod,
  investmentAmount,
  onSelect,
  onContinue,
  canContinue,
}: PaymentMethodStepProps) {
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h1
          ref={headingRef}
          tabIndex={-1}
          className="text-3xl md:text-4xl mb-2 focus-visible:outline-none"
        >
          How would you like to pay?
        </h1>
        <p className="text-lg text-muted-foreground">
          Choose your preferred payment method
        </p>
      </div>

      <div className="space-y-4" role="radiogroup" aria-label="Payment method">
        {/* Bank Transfer */}
        <button
          role="radio"
          aria-checked={paymentMethod === 'bank'}
          onClick={() => onSelect('bank')}
          className={`w-full p-6 rounded-xl border-2 transition text-left ${
            paymentMethod === 'bank'
              ? 'border-primary bg-primary/5'
              : 'border-border bg-card hover:border-primary/50'
          }`}
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-secondary rounded-full flex items-center justify-center flex-shrink-0">
              <Building2 className="w-6 h-6 text-foreground" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-lg font-medium">Bank Transfer (ACH)</h3>
                <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                  Recommended
                </span>
              </div>
              <p className="text-muted-foreground text-sm mb-3">
                Secure direct transfer from your bank account
              </p>
              <ul className="space-y-1 text-sm text-muted-foreground">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-muted-foreground" />
                  <span>No transaction fees</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-muted-foreground" />
                  <span>2–3 business days processing</span>
                </li>
              </ul>
            </div>
            {paymentMethod === 'bank' && (
              <CheckCircle2 className="w-6 h-6 text-foreground flex-shrink-0" />
            )}
          </div>
        </button>

        {/* Cryptocurrency */}
        <button
          role="radio"
          aria-checked={paymentMethod === 'crypto'}
          onClick={() => onSelect('crypto')}
          className={`w-full p-6 rounded-xl border-2 transition text-left ${
            paymentMethod === 'crypto'
              ? 'border-primary bg-primary/5'
              : 'border-border bg-card hover:border-primary/50'
          }`}
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-secondary rounded-full flex items-center justify-center flex-shrink-0">
              <Wallet className="w-6 h-6 text-foreground" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-medium mb-1">Cryptocurrency</h3>
              <p className="text-muted-foreground text-sm mb-3">
                Pay with ETH, USDC, or XRP
              </p>
              <ul className="space-y-1 text-sm text-muted-foreground">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-muted-foreground" />
                  <span>Instant processing</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-muted-foreground" />
                  <span>Network gas fees apply</span>
                </li>
              </ul>
            </div>
            {paymentMethod === 'crypto' && (
              <CheckCircle2 className="w-6 h-6 text-foreground flex-shrink-0" />
            )}
          </div>
        </button>

        {/* Wire Transfer */}
        <button
          role="radio"
          aria-checked={paymentMethod === 'wire'}
          onClick={() => onSelect('wire')}
          className={`w-full p-6 rounded-xl border-2 transition text-left ${
            paymentMethod === 'wire'
              ? 'border-primary bg-primary/5'
              : 'border-border bg-card hover:border-primary/50'
          }`}
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-secondary rounded-full flex items-center justify-center flex-shrink-0">
              <Banknote className="w-6 h-6 text-foreground" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-medium mb-1">Wire Transfer</h3>
              <p className="text-muted-foreground text-sm mb-3">
                For larger investments via bank wire
              </p>
              <ul className="space-y-1 text-sm text-muted-foreground">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-muted-foreground" />
                  <span>Same-day or next-day settlement</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-muted-foreground" />
                  <span>Wire instructions provided after review</span>
                </li>
              </ul>
            </div>
            {paymentMethod === 'wire' && (
              <CheckCircle2 className="w-6 h-6 text-foreground flex-shrink-0" />
            )}
          </div>
        </button>

        {/* Investment Account */}
        <button
          role="radio"
          aria-checked={paymentMethod === 'investment_account'}
          onClick={() => onSelect('investment_account')}
          className={`w-full p-6 rounded-xl border-2 transition text-left ${
            paymentMethod === 'investment_account'
              ? 'border-primary bg-primary/5'
              : 'border-border bg-card hover:border-primary/50'
          }`}
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-secondary rounded-full flex items-center justify-center flex-shrink-0">
              <Landmark className="w-6 h-6 text-foreground" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-medium mb-1">Investment Account</h3>
              <p className="text-muted-foreground text-sm mb-3">
                Fund from a brokerage or custodial account
              </p>
              <ul className="space-y-1 text-sm text-muted-foreground">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-muted-foreground" />
                  <span>IRA, 401(k), self-directed accounts</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-muted-foreground" />
                  <span>Custodian transfer instructions provided</span>
                </li>
              </ul>
            </div>
            {paymentMethod === 'investment_account' && (
              <CheckCircle2 className="w-6 h-6 text-foreground flex-shrink-0" />
            )}
          </div>
        </button>
      </div>

      {/* ACH note for amounts under $25k */}
      {paymentMethod === 'bank' && investmentAmount < 25000 && (
        <div className="rounded-xl border-2 border-border bg-card p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
            <p className="text-sm text-muted-foreground">
              For ACH transfers under {formatCurrency(25000)}, a brief verification call is required.
              Investments of {formatCurrency(25000)}+ process without additional verification.
            </p>
          </div>
        </div>
      )}

      <Button
        className="w-full h-14 text-lg disabled:opacity-40"
        onClick={onContinue}
        disabled={!canContinue}
      >
        Continue
      </Button>
    </div>
  );
}
